import { useCallback } from "react";
import { useNavigate } from "react-router";
import z from "zod";

import getAuthClient from "@skilitics/designer-auth";
import { useFeatureSwitch } from "@skilitics/designer-auth/state";
import { Button, FieldText, FormField, FormikForm, Loading, PageSection } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { useWorkspaceInfo, useInvalidateWorkspaceList } from "../../../../auth/workspaces.ts";
import { useAction } from "../../../../shared/useAction.ts";
import { DangerSummary, DangerZoneSection } from "../DangerZoneSection.tsx";
import { ButtonsWrapper, LayoutForm, LayoutGroup } from "../styled.tsx";

type FormValues = {
  name: string;
};

const validationSchema = z.object({
  name: z.string().min(1).max(256),
});

export const WorkspaceGeneralPage = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const workspace = useWorkspaceInfo();
  const invalidateWorkspaceList = useInvalidateWorkspaceList();
  const navigate = useNavigate();
  const [action, pending] = useAction();
  const showWorkspaceActions = useFeatureSwitch("org-admin");

  const leaveWorkspace = useCallback(async () => {
    await action(async () => {
      await getAuthClient().leaveWorkspace();
      await invalidateWorkspaceList();
      await navigate("/settings");
    });
  }, [action, invalidateWorkspaceList, navigate]);

  const handleUpdateWorkspace = async (values: FormValues) => {
    await getAuthClient().updateWorkspaceName(values.name);
    // The workspace list is not maintained by the AdminClient,
    // we need to tell react-query to update.
    await invalidateWorkspaceList();
  };

  // When switching workspace we don't want layout shift
  if (!workspace) {
    return null;
  }

  const isAdmin = workspace?.role === "admin";
  return (
    <>
      {isAdmin && (
        <PageSection
          header={
            <>
              <h3>{formatMessage("header")}</h3>
              <p>{formatMessage("description")}</p>
            </>
          }
        >
          <LayoutGroup>
            <FormikForm<FormValues>
              initialValues={{ name: workspace?.name ?? "" }}
              validationSchema={validationSchema}
              formatStrings={formatStrings}
              onSubmit={handleUpdateWorkspace}
            >
              {({ dirty, isValid, isSubmitting }) => (
                <LayoutForm $gridColumns="300px auto" blurOnEnter={false}>
                  <FormField<FormValues> name="name">
                    <FieldText disabled={!workspace} />
                  </FormField>
                  {dirty && (
                    <ButtonsWrapper>
                      <Button type="submit" variant="contained" disabled={!isValid || isSubmitting}>
                        {isSubmitting && <Loading inline size={12} />} Save
                      </Button>
                      <Button type="reset" color="inherit">
                        Cancel
                      </Button>
                    </ButtonsWrapper>
                  )}
                </LayoutForm>
              )}
            </FormikForm>
          </LayoutGroup>
        </PageSection>
      )}

      {showWorkspaceActions && (
        <DangerZoneSection>
          <DangerSummary>
            <h3>Leave workspace: {workspace?.name ?? "-"}</h3>
            <p>If you leave this workspace, you will...</p>
            <ul>
              <li>Lose access to all content within the workspace.</li>
              <li>Lose access to this workspace.</li>
            </ul>
            <p>You must be re-invited by a workspace admin to regain access.</p>
          </DangerSummary>
          <div>
            <Button variant="contained" color="error" disabled={isAdmin || pending} onClick={leaveWorkspace}>
              Leave Workspace
            </Button>
          </div>
          {isAdmin && (
            <em>
              Workspace admins can&apos;t leave a workspace to avoid accidentally orphaning content. You can either
              remove the workspace (when implemented) or get another worksapce admin to remove you.
            </em>
          )}

          {isAdmin && (
            <>
              <DangerSummary>
                <h3>Remove Workspace</h3>
                <p>Removing this workspace will have the following effects:</p>
                <ul>
                  <li>
                    <strong>All members</strong> of the workspace <strong>will lose access to the content.</strong>
                  </li>
                  <li>
                    <strong>All content</strong> belonging to this workspace <strong>will be deleted.</strong>
                  </li>
                  <li>
                    You will <strong>no longer have access</strong> to this workspace and its content.
                  </li>
                  <li>This change will take effect immediately.</li>
                </ul>
                <p>
                  <strong className="color">
                    This action cannot be undone, and your content will be gone forever.
                  </strong>
                </p>
              </DangerSummary>
              <div>
                <Button variant="contained" color="error" disabled>
                  Remove Workspace
                </Button>
              </div>
              <em>Workspace removal is not implemented yet</em>
            </>
          )}
        </DangerZoneSection>
      )}
    </>
  );
};

const formatStrings = {
  header: { en: "Workspace Details" },
  description: { en: "Manage your workspace details." },
  nameLabel: { en: "Workspace Name" },
};
